import * as fs from 'fs/promises';
import * as path from 'path';
import { glob } from 'glob';
import { VibeshipConfig } from '../config/types';
import { PathValidator } from './security/path-validator';
import { FileInfo, ScanOptions, ScanProgress } from './types';

export class FileScanner {
  private pathValidator: PathValidator;
  private progressCallback?: (progress: ScanProgress) => void;

  constructor(private config: VibeshipConfig) {
    this.pathValidator = new PathValidator(config.scanPaths);
  }

  /**
   * Scan for files matching the configured patterns
   */
  async scan(options?: ScanOptions): Promise<string[]> {
    const files: string[] = [];
    const scanPaths = options?.paths || this.config.scanPaths;
    const includePatterns = options?.include || this.config.include;
    const excludePatterns = options?.exclude || this.config.exclude;
    
    let totalScanned = 0;
    const totalPaths = scanPaths.length;
    
    for (const scanPath of scanPaths) {
      // Validate scan path
      if (!this.pathValidator.isValid(scanPath)) {
        throw new Error(`Invalid scan path: ${scanPath}`);
      }

      try {
        // Check if scanPath is a file or directory
        const stats = await fs.stat(scanPath).catch(() => null);
        
        if (stats?.isFile()) {
          // If it's a file, check if it matches include patterns and add directly
          const fileName = path.basename(scanPath);
          const shouldInclude = includePatterns.some((pattern: string) => {
            // Simple pattern matching for file extensions
            if (pattern.includes('*')) {
              const ext = pattern.replace('**/', '').replace('*', '');
              return fileName.endsWith(ext);
            }
            return fileName === pattern;
          });
          
          if (shouldInclude) {
            files.push(path.resolve(scanPath));
          }
        } else if (stats?.isDirectory()) {
          // If it's a directory, use glob as before
          for (const pattern of includePatterns) {
            const matches = await glob(pattern, {
              cwd: scanPath,
              ignore: excludePatterns,
              absolute: true,
              nodir: true,
              dot: true,
            });
            
            files.push(...matches);
          }
        } else {
          // Path doesn't exist, try glob from current directory
          const matches = await glob(scanPath, {
            ignore: excludePatterns,
            absolute: true,
            nodir: true,
            dot: true,
          });
          
          // Filter matches by include patterns
          for (const match of matches) {
            const shouldInclude = includePatterns.some((pattern: string) => {
              const ext = pattern.replace('**/', '').replace('*', '');
              return match.endsWith(ext);
            });
            if (shouldInclude) {
              files.push(match);
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to scan path ${scanPath}:`, error);
      }
      
      totalScanned++;
      
      // Report progress
      if (this.progressCallback) {
        this.progressCallback({
          current: totalScanned,
          total: totalPaths,
          currentPath: scanPath,
          filesFound: files.length,
        });
      }
    }
    
    // Remove duplicates
    return [...new Set(files)];
  }

  /**
   * Scan with detailed file information
   */
  async scanWithInfo(options?: ScanOptions): Promise<FileInfo[]> {
    const filePaths = await this.scan(options);
    const fileInfos: FileInfo[] = [];
    
    for (const filePath of filePaths) {
      try {
        const stats = await fs.stat(filePath);
        const content = await this.readFile(filePath);
        
        fileInfos.push({
          path: filePath,
          content,
          stats: {
            size: stats.size,
            modified: stats.mtime,
          },
        });
      } catch (error) {
        // Skip files that can't be read
        console.warn(`Failed to read file ${filePath}:`, error);
      }
    }
    
    return fileInfos;
  }

  /**
   * Read a file with validation
   */
  async readFile(filePath: string): Promise<string> {
    // Validate file path
    if (!this.pathValidator.isValid(filePath)) {
      throw new Error(`Access denied: ${filePath}`);
    }
    
    try {
      return await fs.readFile(filePath, 'utf-8');
    } catch (error: any) {
      if (error.code === 'ENOENT') {
        throw new Error(`File not found: ${filePath}`);
      }
      if (error.code === 'EACCES') {
        throw new Error(`Permission denied: ${filePath}`);
      }
      throw error;
    }
  }

  /**
   * Check if a file exists
   */
  async exists(filePath: string): Promise<boolean> {
    try {
      await fs.access(filePath);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get file stats
   */
  async getStats(filePath: string): Promise<Awaited<ReturnType<typeof fs.stat>>> {
    if (!this.pathValidator.isValid(filePath)) {
      throw new Error(`Access denied: ${filePath}`);
    }
    
    return fs.stat(filePath);
  }

  /**
   * Set progress callback for long operations
   */
  onProgress(callback: (progress: ScanProgress) => void): void {
    this.progressCallback = callback;
  }

  /**
   * Get supported file extensions from include patterns
   */
  getSupportedExtensions(): string[] {
    const extensions = new Set<string>();
    
    this.config.include.forEach((pattern: string) => {
      const match = pattern.match(/\*\.(\w+)$/);
      if (match) {
        extensions.add(`.${match[1]}`);
      }
    });
    
    return Array.from(extensions);
  }
}